import React, { useState, useEffect, useRef } from 'react'
import { Formik } from 'formik'
import {
  ExperienceInputField,
  ExperienceEmailMultiSelectField,
  ExperienceCheckbox,
} from '../form'
import QuestHOC from './QuestHOC'
import PropTypes from 'prop-types'
import {
  runQueuedFormikSave,
  FormikDirtyReporter,
  SetupNameReporter,
} from './questAutosave'
import ExperienceBadge from './ExperienceBadge'

const mapLeaderEmails = (emails) =>
  emails && Array.isArray(emails)
    ? emails.map((email) => ({
        value: typeof email === 'string' ? email : email.value || email,
        label:
          typeof email === 'string'
            ? email
            : email.label || email.value || email,
      }))
    : []

const valuesFromQuest = (questDetail) => ({
  name: questDetail?.name || '',
  title: questDetail?.title || '',
  description: questDetail?.description || '',
  programType: questDetail?.programType || '',
  isVisible:
    questDetail?.isVisible !== undefined ? questDetail.isVisible : true,
  questLeadersEmails: mapLeaderEmails(questDetail?.questLeadersEmails),
})

const validate = (values) => {
  const errors = {}
  if (!(values.name || '').trim()) {
    errors.name = 'Required'
  }
  return errors
}

const buildPayload = (values) => {
  const {
    _questLeadersEmails_hasError,
    _questLeadersEmails_inputValue,
    ...cleanValues
  } = values
  return {
    ...cleanValues,
    name: (cleanValues.name || '').trim(),
    title: (cleanValues.title || '').trim(),
    description: (cleanValues.description || '').trim().replace(/^"|"$/g, ''),
    questLeadersEmails:
      cleanValues.questLeadersEmails &&
      Array.isArray(cleanValues.questLeadersEmails)
        ? cleanValues.questLeadersEmails.map((item) => item.value || item)
        : [],
  }
}

const ExperienceDetails = ({
  questDetail,
  updateQuestDetails,
  isFormSubmitting,
  isSaving,
}) => {
  const busy = !!isFormSubmitting || !!isSaving
  const [initialValues, setInitialValues] = useState(() =>
    valuesFromQuest(questDetail),
  )
  const valuesRef = useRef(initialValues)
  const [hasEmailError, setHasEmailError] = useState(false)
  const hasEmailErrorRef = useRef(false)

  useEffect(() => {
    hasEmailErrorRef.current = hasEmailError
  }, [hasEmailError])

  // One save at a time, and never re-send what was already sent. Blur autosave
  // and Next's flush can fire back-to-back; on a new experience the second
  // call would otherwise create the quest twice.
  const inFlightRef = useRef(null)
  const lastSavedRef = useRef(JSON.stringify(buildPayload(initialValues)))

  // Autosave must not be blocked by the required-field rules (those only
  // apply when the user clicks Next), so the draft keeps whatever is filled.
  const persist = async (values, resetForm) => {
    while (inFlightRef.current) {
      await inFlightRef.current.catch(() => {})
    }
    if (JSON.stringify(buildPayload(values)) === lastSavedRef.current) return
    const run = runQueuedFormikSave({
      values,
      valuesRef,
      buildPayload,
      resetForm,
      setInitialValues,
      shouldSkip: () => hasEmailErrorRef.current,
      save: async (payload) => {
        const previous = lastSavedRef.current
        lastSavedRef.current = JSON.stringify(payload)
        try {
          await updateQuestDetails(payload, { silent: true })
        } catch (e) {
          lastSavedRef.current = previous
          throw e
        }
      },
    })
    inFlightRef.current = run
    try {
      await run
    } finally {
      inFlightRef.current = null
    }
  }
  const saveNow = (formik, values) => persist(values, formik.resetForm)

  useEffect(() => {
    const next = valuesFromQuest(questDetail)
    setInitialValues(next)
    valuesRef.current = next
    lastSavedRef.current = JSON.stringify(buildPayload(next))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questDetail?._id, questDetail?.id])

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={validate}
      onSubmit={(values, { resetForm }) => persist(values, resetForm)}>
      {(formik) => {
        const {
          handleSubmit,
          values,
          setFieldValue,
          submitForm,
          dirty,
          submitCount,
        } = formik
        // Required errors appear only after the user tries to continue.
        const errorVisible = submitCount > 0
        valuesRef.current = formik.values

        const fieldBlur = () => {
          window.setTimeout(() => {
            if (formik.dirty) saveNow(formik, valuesRef.current)
          }, 0)
        }

        return (
          <form onSubmit={handleSubmit} aria-busy={busy}>
            <FormikDirtyReporter id='setup-name' dirty={dirty} />
            <SetupNameReporter name={values.name} />
            <div className='experience-f-row'>
              <ExperienceInputField
                name='name'
                label='Internal name'
                where={['admin']}
                placeholder='Add an internal name'
                showErrorMessage
                errorVisible={errorVisible}
                capitalizeFirstLetter
                onBlur={fieldBlur}
              />
              <ExperienceInputField
                name='title'
                label='Public title'
                where={['pub', 'par']}
                placeholder='Add a public title'
                showErrorMessage
                errorVisible={errorVisible}
                capitalizeFirstLetter
                hint='This is the title people read — on Prosperity and in their experience. Write it as they should see it.'
                hintClassName='text-dark'
                onBlur={fieldBlur}
              />
            </div>

            <div className='experience-f-row'>
              <ExperienceInputField
                name='programType'
                label='Experience Program Type'
                where={['pub']}
                placeholder='Choose programme'
                showErrorMessage
                errorVisible={errorVisible}
                hint='Free text. Clients see this exactly as written — Quest, Pilgrimage, Retreat, etc.'
                hintClassName='text-dark'
                onBlur={fieldBlur}
              />
              <ExperienceEmailMultiSelectField
                name='questLeadersEmails'
                label='Experience Leader Emails'
                where={['admin']}
                placeholder='Enter email addresses'
                maxMenuHeight={140}
                onValidationChange={setHasEmailError}
                onBlur={fieldBlur}
              />
            </div>

            <div className='experience-f-row'>
              <ExperienceInputField
                name='description'
                label='Summary'
                where={['pub']}
                placeholder='Add a short summary'
                as='textarea'
                rows={4}
                showErrorMessage
                errorVisible={errorVisible}
                capitalizeFirstLetter
                onBlur={fieldBlur}
              />
            </div>

            <div className='experience-check'>
              <ExperienceCheckbox
                name='isVisible'
                checked={!!values.isVisible}
                label={
                  <>
                    Show in open experiences{' '}
                    <ExperienceBadge title='Prosperity' variant='blue' />
                  </>
                }
                onChange={(e) => {
                  const next = e.target.checked
                  setFieldValue('isVisible', next, false)
                  saveNow(formik, { ...valuesRef.current, isVisible: next })
                }}
              />
            </div>
            <button type='submit' hidden disabled={!submitForm} aria-hidden />
          </form>
        )
      }}
    </Formik>
  )
}

ExperienceDetails.propTypes = {
  questDetail: PropTypes.shape({
    name: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    programType: PropTypes.string,
    isVisible: PropTypes.bool,
  }),
  updateQuestDetails: PropTypes.func.isRequired,
  isFormSubmitting: PropTypes.bool,
  isSaving: PropTypes.bool,
}

export default QuestHOC(ExperienceDetails)
